import React, { useContext, useEffect, useState } from "react";
import { useSurvey } from "./SurveyContext";
import { ContentProps } from "./types";
import QuestionSections from "../components/QuestionSection";
import { SecondaryButton } from "../../../../components/Modules/SecondaryButton";
import { PrimaryButton } from "../../../../components/Modules/PrimaryButton";
import { useGeneral } from "../../../../general";
import QuestWrapper from "../../../../components/QuestWrapper";

interface ValidationState {
  [key: string]: {
    isValid: boolean;
    regexValid: boolean;
  };
}

export const Content: React.FC<ContentProps> = ({
  ratingType = "number",
  className,
}) => {
  const {
    sectionQuestions,
    answer,
    handleUpdate,
    handleRadioChange,
    handleRatingChange,
    selectedOptions,
    handleCheckboxChange,
    sections,
    themeManager,
    currentSection,
    setCurrentSection,
    design,
    handleReturnAnswers,
    goToNextSection,
    GeneralFunctions,
    onCancel,
    showCancelButton = true,
    submitButtonText,
    userId,
    uniqueUserId,
    token,
    singleChoiceModal,
    multiChoiceModal,
  } = useSurvey();

  const [validationState, setValidationState] = useState<ValidationState>({});
  const defaultEmailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const {user} = useContext(QuestWrapper.Context)
  const general = useGeneral('quest')

  const validateField = (questionId: string, value: any, question: any) => {
    let isValid = true;
    let regexValid = true;

    if (question.required) {
      switch (question.type) {
        case "USER_INPUT_MULTI_CHOICE":
          isValid = selectedOptions[questionId]?.length > 0;
          break;
        case "USER_INPUT_SINGLE_CHOICE":
          isValid = !!answer[questionId];
          break;
        case "RATING":
          isValid =
            answer[questionId] !== undefined && answer[questionId] !== "";
          break;
        case "USER_INPUT_TEXT":
        case "USER_INPUT_EMAIL":
        case "USER_INPUT_TEXTAREA":
        case "USER_INPUT_DATE":
        case "USER_INPUT_PHONE":
        case "USER_INPUT_NUMBER":
          isValid = value !== undefined && value !== "";
          break;
        default:
          isValid = !!value;
      }
    }

    if (question.type === "USER_INPUT_EMAIL" && value) {
      if (question.regexKey) {
        try {
          const regex = new RegExp(question.regexKey);
          regexValid = regex.test(value.toString());
        } catch (e) {
          console.error("Invalid regex pattern:", e);
          regexValid = defaultEmailPattern.test(value.toString());
        }
      } else {
        regexValid = defaultEmailPattern.test(value.toString());
      }
    } else if (question.regexKey && value) {
      try {
        const regex = new RegExp(question.regexKey);
        regexValid = regex.test(value.toString());
      } catch (e) {
        console.error("Invalid regex pattern:", e);
        regexValid = true;
      }
    }

    return { isValid, regexValid };
  };

  useEffect(() => {
    if (sectionQuestions) {
      const newValidationState: ValidationState = {};

      sectionQuestions.forEach((question: any) => {
        const value = answer[question.actionId];
        const validation = validateField(question.actionId, value, question);

        newValidationState[question.actionId] = {
          isValid: validation.isValid,
          regexValid: validation.regexValid,
        };
      });

      setValidationState(newValidationState);
    }
  }, [sectionQuestions, currentSection]);

  useEffect(() => {
    if (sectionQuestions) {
      const newValidationState = { ...validationState };

      sectionQuestions.forEach((question: any) => {
        const value = answer[question.actionId];
        const validation = validateField(question.actionId, value, question);

        newValidationState[question.actionId] = {
          isValid: validation.isValid,
          regexValid: validation.regexValid,
        };
      });

      setValidationState(newValidationState);
    }
  }, [answer, selectedOptions]);

  const handleRegexValidation = (validated: boolean, actionId: string) => {
    if (!validationState[actionId]) return;

    setValidationState((prev) => ({
      ...prev,
      [actionId]: {
        ...prev[actionId],
        regexValid: validated,
      },
    }));
  };

  const isCurrentSectionValid = React.useMemo(() => {
    if (!sectionQuestions || sectionQuestions.length === 0) return true;

    return sectionQuestions.every((question) => {
      const validation = validationState[question.actionId];
      if (!validation) return false;

      const value = answer[question.actionId];

      if (question.type === "USER_INPUT_EMAIL") {
        if (question.required) {
          return validation.isValid && validation.regexValid;
        }
        return !value || validation.regexValid;
      }

      if (question.required && question.regexKey) {
        return validation.isValid && (value ? validation.regexValid : false);
      }

      if (question.required) {
        return validation.isValid;
      }

      if (question.regexKey && value) {
        return validation.regexValid;
      }

      return true;
    });
  }, [validationState, sectionQuestions, answer]);
  

  return (
    <div className={`q-survey-content ${className || ""}`}>
      <form
        style={{
          padding: "20px",
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          boxSizing: "border-box"
        }}
      >
        {sectionQuestions?.map((data: any, index: number) => {
          const key = data?.criteriaId || index;
          return (
            <QuestionSections
              key={key}
              data={data}
              answer={answer}
              handleUpdate={handleUpdate}
              handleRadioChange={handleRadioChange}
              handleRatingChange={handleRatingChange}
              selectedOptions={selectedOptions}
              handleCheckboxChange={handleCheckboxChange}
              sections={sections}
              themeManager={themeManager}
              singleChoiceModal={singleChoiceModal}
              multiChoiceModal={multiChoiceModal}
              ratingType={ratingType}
              validateInputRegex={(validated) =>
                handleRegexValidation(validated, data.actionId)
              }
            />
          );
        })}

        <div className="q_feedback_buttons">
          {showCancelButton && (
            <SecondaryButton
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (currentSection == 0) {
                  onCancel && onCancel();
                }
                if (currentSection > 0) {
                  setCurrentSection(currentSection - 1);
                }
              }}
              style={themeManager.getButtonStyles().secondary}
            >
              {currentSection > 0 ? "Previous" : "Cancel"}
            </SecondaryButton>
          )}

          <PrimaryButton
            type="button"
            style={themeManager.getButtonStyles().primary}
            onClick={() => {
              const totalSections = Object.keys(design);

              if (currentSection < totalSections.length - 1) {
                setCurrentSection(currentSection + 1);
              } else if (currentSection >= totalSections.length - 1) {
                handleReturnAnswers();

                general.trackEvent(
                  'quest-survey-submitted',
                  (uniqueUserId || userId || user.userId)!,
                  {},
                  true,
                  !uniqueUserId
                )
              }
            }}
            disabled={!isCurrentSectionValid}
          >
            {currentSection >= design.length - 1 ? (submitButtonText || "Submit") : "Next"}
          </PrimaryButton>
        </div>
      </form>
    </div>
  );
};
